import common from '../common';
import Scene from './Scene';
import World from '../environment/world';
import CommandInterface from '../commands/CommandInterface';
import CameraController from '../controllers/CameraController';
import {translate, Text} from '../utils';
import GoalTile from '../traps/GoalTile';
import ParticleEmitter from '../particles/ParticleEmitter';
import Player from '../physics/Player';
import screenfull from 'screenfull';
import TransformEffect from '../transform-effect/transform-effect';
import TutorialOverlay from '../ui/tutorial/TutorialOverlay';
import FocusOverlay from '../ui/tutorial/FocusOverlay';

export default class GameScene extends Scene {
	constructor(level = 0) {
		super();
		this.level = Number(level);
		this.collectablesPanel = null;
		this.cameraController = null;
		this.commandinterface = null;
		this.underwaterSprite = null;
		this.backgroundMusic = null;
		this.blockDisplay = null;
		this.focusOverlay = null;
		this.pauseButton = null;
		this.coinDisplay = null;
		this.playButton = null;
		this.runButton = null;
		this.ambience = null;
		this.overlay = null;
		this.player = null;
		this.world = null;
		this.greenParticles = null;
		this.transformEffect = null;
		this.cameraTweening = false;
		this.overheadActive = false;
		this.tutorialMode = false;
		this.benScreenVector = new THREE.Vector3();
		this.signals.fakeload = new P3.utils.MiniSignal();
		this._fullTint = null;
	}

	init() {
		super.init();

		while (common.THREEstage.children.length > 0) {
			common.THREEstage.remove(common.THREEstage.children[0]);
		}

		common.currentLevel = this.level;
		common.levelSettings = P3.assets.json('level_data')[this.level];

		if (common.levelSettings.path.includes('laboratory')) {
			common.environment = 0;
		} else if (common.levelSettings.path.includes('temple')) {
			common.environment = 1;
		} else if (common.levelSettings.path.includes('mine')) {
			common.environment = 2;
		}

		common.t.track('game phases milestones', {gamelevel: common.currentLevel, gamemilestones: 1});

		console.log(`Current Level: ${common.currentLevel} | LevelID: ${common.levelSettings.filename}`);

		this.tutorialMode = common.levelSettings.tutorial !== 'none';

		common.world = this.world = new World();
		common.player = this.player = this.world.player;

		if (this.tutorialMode) {
			this.focusOverlay = new FocusOverlay();
			this.focusOverlay.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
			this.focusOverlay.visible = false;
			common.focusOverlay = this.focusOverlay;
		}

		this.commandinterface = new CommandInterface(this.player, this, this.tutorialMode);
		this.commandinterface.signals.levelcomplete.once(this.animateCoin, this);

		if (this.tutorialMode) {
			this.overlay = new TutorialOverlay(this, this.commandinterface);
		}

		let states = new P3.ButtonStates();
		states.normal = P3.assets.texture('ui_btn_small_out');
		states.over = P3.assets.texture('ui_btn_small_over');
		states.down = P3.assets.texture('ui_btn_small_down');
		states.icon = P3.assets.texture('ui_icon_pause');

		this.pauseButton = new P3.Button(states);
		this.pauseButton.clickSoundName = 'sfx_btn_press_00';
		this.pauseButton.overSoundName = 'sfx_btn_rollover_00';
		this.pauseButton.signals.click.add(() => {
			this.transformEffect.pause();
			this.signals.pause.dispatch(this);
		}, this);
		this.pauseButton.position.set(((common.STAGE_WIDTH + P3.View.width) * 0.5) - 75, 75);

		// transform effect
		this.transformEffect = new TransformEffect();
		this.addChild(this.transformEffect);
		common.player.signals.transformEffect.add(this.onTransform, this);

		// collectables
		this.collectablesPanel = new PIXI.mesh.NineSlicePlane(P3.assets.texture('collectables_panel'), 50, 10, 50, 10);
		this.collectablesPanel.width = 700;
		this.collectablesPanel.position.set(common.STAGE_WIDTH * 0.5 - this.collectablesPanel.width * 0.5, 10);
		this.addChild(this.collectablesPanel);

		this.coinDisplay = new PIXI.Sprite(P3.assets.texture('ui_icon_omnitrix_end'));
		this.coinDisplay.anchor.set(0.5);
		this.coinDisplay.scale.set(0.8);
		this.coinDisplay.position.set(this.collectablesPanel.width * 0.5 + 100, this.collectablesPanel.height * 0.5);
		this.collectablesPanel.addChild(this.coinDisplay);

		// override json file
		common.levelSettings.coins = this.world.getCoins();

		this.coinDisplay.label = Text(`${this.player.coins}/${common.levelSettings.coins}`, {font: '36px Ahkio Thin', align: 'center'});
		this.coinDisplay.label.tint = 0x90BF1C;
		this.coinDisplay.label.anchor.set(0, 0.5);
		this.coinDisplay.label.position.set(this.coinDisplay.x + (this.coinDisplay.width * 0.5) + 5, this.coinDisplay.y);
		this.collectablesPanel.addChild(this.coinDisplay.label);

		this.blockDisplay = new PIXI.mesh.NineSlicePlane(P3.assets.texture('ui_function_9slice_playback'), 80, 0, 20, 0);
		this.blockDisplay.width = 75;
		this.blockDisplay.height = 50;
		this.blockDisplay.tint = 0x00FF00;
		this.blockDisplay.position.set(this.collectablesPanel.width * 0.5 - 188, this.collectablesPanel.height * 0.5 - 20);
		this.collectablesPanel.addChild(this.blockDisplay);

		this.blockDisplay.label = Text(`${0}/${common.levelSettings.minimumBlocks}`, {font: '36px Ahkio Thin', align: 'center'});
		this.blockDisplay.label.tint = 0x90BF1C;
		this.blockDisplay.label.anchor.set(0.5);
		this.blockDisplay.label.position.set(this.blockDisplay.x + 110, this.coinDisplay.label.y);
		this.collectablesPanel.addChild(this.blockDisplay.label);

		this.player.signals.coinpickup.add(() => {
			this.coinDisplay.label.text = `${this.player.coins}/${common.levelSettings.coins}`;
		});

		this.commandinterface.signals.blockchange.add((e) => {
			this.blockDisplay.label.text = `${e}/${common.levelSettings.minimumBlocks}`;
		});

		this.underwaterSprite = new PIXI.Graphics();
		this.underwaterSprite.beginFill(0x06917c, 0.3);
		this.underwaterSprite.drawRect(0, 0, common.STAGE_WIDTH, common.STAGE_HEIGHT);
		this.underwaterSprite.visible = true;
		common.stage.addChildAt(this.underwaterSprite, 0);

		states = new P3.ButtonStates();
		states.normal = P3.assets.texture('ui_btn_small_out');
		states.over = P3.assets.texture('ui_btn_small_over');
		states.down = P3.assets.texture('ui_btn_small_down');
		states.icon = P3.assets.texture('ui_icon_view');

		this.cameraButton = new P3.Button(states);
		this.cameraButton.clickSoundName = 'sfx_btn_press_00';
		this.cameraButton.overSoundName = 'sfx_btn_rollover_00';

		this.cameraButton.signals.click.add(() => {
			this.toggleOverheadCamera();
		}, this);

		this.addChild(this.cameraButton);

		// set camera bounds
		const bounds = this.world.getBounds();
		const margin = 0.4;

		common.levelSettings.camera_limits.xmin = bounds.xmin + margin;
		common.levelSettings.camera_limits.xmax = bounds.xmax - margin;
		common.levelSettings.camera_limits.zmin = bounds.zmin + margin;
		common.levelSettings.camera_limits.zmax = bounds.zmax - margin;

		this.cameraController = new CameraController(this.player);

		this.toggleCamera();

		states = new P3.ButtonStates();
		states.normal = P3.assets.texture('ui_btn_run_out');
		states.over = P3.assets.texture('ui_btn_run_over');
		states.down = P3.assets.texture('ui_btn_run_down');

		this.runButton = new P3.Button(states);
		this.runButton.clickSoundName = 'sfx_btn_press_00';
		this.runButton.overSoundName = 'sfx_btn_rollover_00';
		this.runButton.visible = false;

		this.runButton.signals.click.add(() => {
			if (this.overheadActive) {
				this.commandinterface.lock();
				this.toggleOverheadCamera(() => {
					this.commandinterface.start();
				});
			} else {
				this.commandinterface.lock();
				this.tweenToStart(() => {
					this.commandinterface.start();
				});
			}
		}, this);

		this.runButton.position.set((common.STAGE_WIDTH * 0.5), common.STAGE_HEIGHT - 100);

		if (common.levelSettings.tutorial !== 'none') {
			this.runButton.y = common.STAGE_HEIGHT - 240;
		}

		const runText = Text(translate('GAME.BUTTONS.RUN'), {font: '32px Ahkio Regular', align: 'center'});
		runText.tint = 0xFFFFFF;
		runText.maxWidth = this.runButton.width - 40;
		runText.anchor.set(0.5);
		runText.position.set(0, 0);
		this.runButton._icon.addChild(runText);

		this.commandinterface.signals.setvisibility.add((state) => {
			this.runButton.visible = state;

			if (this.overlay && this.overlay.panel && this.overlay.panel.visible) {
				this.runButton.y = common.STAGE_HEIGHT - 240;

				if (this.overlay.panel.icon) {
					this.runButton.y -= 35;
				}
			}
		});

		this.commandinterface.signals.start.add(() => {
			if (this.tutorialMode) {
				this.overlay.stop();
			}

			this.runButton.visible = false;
			this.cameraButton.visible = false;

			// disables the camera controllers and adds the camera to the player so it follows the player
			const pos = this.player.mesh.position;
			this.cameraController.sideControls.enabled = false;
			this.cameraController.overheadControls.enabled = false;
			this.player.mesh.add(this.camera);
			this.camera.position.copy(new THREE.Vector3(0, 0, 0));
			this.camera.position.add(new THREE.Vector3(1.8, 2.6, 6));
			this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));
		}, this);

		this.commandinterface.signals.end.add(() => {
			this.cameraController.sideControls.reset();
			this.cameraButton.visible = true;

			this.world.resetTraps();
			this.runButton.visible = true;

			common.THREEstage.add(this.camera);
			this.cameraController.sideControls.enabled = true;
			const pos = this.player.mesh.position;
			this.runButton.position.y = this.overlay && this.overlay.panel.visible ? common.STAGE_HEIGHT - 240 : common.STAGE_HEIGHT - 75;
			this.camera.position.copy(pos).add(new THREE.Vector3(1.8, 2.6, 6));
			this.cameraController.sideControls.target.copy(pos).add(new THREE.Vector3(1.8, 2.6, 0));
			this.cameraController.sideControls.update();		// update once to stop a single frame flicker.
		}, this);

		this.commandinterface.signals.updateTutorial.add(stage => {
			this.overlay.updateTutorial(stage);
		});

		let levelOnePlayed = false;

		this.cameraController.sideControls.signals.onMouseMove.add((distance) => {
			this.world.hideObjects();
			if (!this.tutorialMode || common.currentLevel !== 0 || levelOnePlayed || distance < 300) {
				return;
			}
			levelOnePlayed = true;
			this.commandinterface.tutorialStage = 1;
			this.overlay.updateTutorial(this.commandinterface.tutorialStage);
		});

		let levelTwoPlayed = false;

		this.cameraController.overheadControls.signals.onMouseMove.add((distance) => {
			if (!this.tutorialMode || common.currentLevel !== 2 || levelTwoPlayed || distance < 100) {
				return;
			}
			levelTwoPlayed = true;
			this.commandinterface.tutorialStage = 2;
			this.overlay.updateTutorial(this.commandinterface.tutorialStage);
		});

		this.addChild(this.commandinterface.ui);
		this.addChild(this.runButton);
		this.backgroundMusic = P3.audio.playMusic(P3.utils.selectRandom(['music_gameplay_loop_01', 'music_gameplay_loop_02']));
		this.ambience = P3.audio.playSound(common.levelSettings.ambient, {loop: true});

		let point = new THREE.Vector3();

		if (!this.world.cameraPath) {
			point.copy(this.player.mesh.position).add(new THREE.Vector3(1.8, 2.6, 6));
		} else {
			point = this.world.cameraPath[0];
		}

		common.camera.position.copy(point);
		common.camera.lookAt(this.player.mesh.position.x + 1.8, this.player.mesh.position.y + 2.6, this.player.mesh.position.z);
		this.player.mesh.quaternion.copy(this.camera.quaternion);

		this.cameraButton.visible = false;
		this.commandinterface.ui.visible = false;

		if (this.tutorialMode) {
			if (common.levelSettings.tutorial === 'full') {
				this.addChild(this.focusOverlay);
			}

			this.addChild(this.overlay);
		}

		this.addChild(this.pauseButton);	// keep pausebutton on top

		this.interactive = true;
		this.on('pointertap', () => {
			if (PIXI.utils.isMobile.any && screenfull.enabled) {
				screenfull.request();
			}
		});

		this._fullTint = new PIXI.Graphics();
		this._fullTint.beginFill(0x0);
		this._fullTint.drawRect((common.STAGE_WIDTH - P3.View.width) * 0.5, 0, P3.View.width, P3.View.height);
		this._fullTint.endFill();
		this.addChild(this._fullTint);

		this.signals.fakeload.dispatch();
	}

	destroy() {
		// clean up goes here
		this.world.destroy();
		this.world = null;
		common.world = null;

		this.commandinterface.destroy();
		this.commandinterface = null;

		this.backgroundMusic && this.backgroundMusic.stop();
		this.backgroundMusic = null;

		this.ambience && this.ambience.stop();
		this.ambience = null;
		P3.audio.stopMusic();

		this.cameraController.overheadControls.enabled = false;
		this.cameraController.overheadControls.reset();

		this.cameraController.sideControls.enabled = false;
		this.cameraController.sideControls.reset();

		this.cameraController.dispose();
		this.cameraController = null;

		for (let emitter of common.particles3D) {
			emitter.dispose();
			emitter = null;
		}

		this.transformEffect.destroy();
		this.transformEffect = null;

		if (this.tutorialMode) {
			this.focusOverlay.destroy({children: true});
			common.focusOverlay = null;
			this.focusOverlay = null;

			this.overlay.destroy({children: true});
			this.overlay = null;
		}

		this.pauseButton.destroy({children: true});
		this.pauseButton = null;

		this.coinDisplay.destroy({children: true});
		this.coinDisplay = null;

		this.collectablesPanel.destroy({children: true});
		this.collectablesPanel = null;

		this.blockDisplay.destroy({children: true});
		this.blockDisplay = null;

		super.destroy();
	}

	onResume() {
		this.transformEffect.play();
	}

	benScreenPosition() {
		// @Martin - I changed this a little to stop creating a new THREE.Vector3 every time this is called, because that's bad for memory.
		// Now uses one vector3 and just sets it. - dave

		const halfWidth = P3.View.width >> 1;
		const halfHeight = P3.View.height >> 1;

		this.benScreenVector.set(this.player.mesh.position.x, this.player.mesh.position.y + 1.8, this.player.mesh.position.z);
		this.benScreenVector.project(common.camera);
		this.benScreenVector.x = Math.round((this.benScreenVector.x + 1) * halfWidth) + ((common.STAGE_WIDTH - P3.View.width) >> 1);
		this.benScreenVector.y = Math.round((-this.benScreenVector.y + 1) * halfHeight);

		return {x: this.benScreenVector.x, y: this.benScreenVector.y};
	}

	onTransform(delay, isFull) {
		this.transformEffect.show(delay, isFull);
	}

	update() {
		super.update();
		this.cameraController.update();
		this.greenParticles && this.greenParticles.update();

		if (this.tutorialMode) {
			this.overlay.update();
			this.focusOverlay.update();
		}

		if ((this.commandinterface.isDragging || this.commandinterface.isScrolling) && !this.cameraTweening) {
			this.cameraController.overheadControls.resetSoft();
			this.cameraController.sideControls.resetSoft();
		} else if (!this.cameraTweening) {
			this.commandinterface.canStart && (this.cameraController.sideControls.enabled = !this.overheadActive);
			this.commandinterface.canStart && (this.cameraController.overheadControls.enabled = this.overheadActive);
		}

		// is the head of our player underwater and is the tile marked as underwater?
		this.underwaterSprite.visible = this.player.isUnderwater = this.player.mesh.position.y + 2 < 0 && this.player.tile.isUnderwater;

		this.commandinterface && this.commandinterface.update();
		this.world && this.world.update();

		for (let i = 0; i < common.particles3D.length; i++) {
			common.particles3D[i].update();

			if (common.particles3D[i].dead) {
				common.particles3D[i].dispose();
				common.particles3D.splice(i, 1);
				break;
			}
		}

		if (!this.test) {
			this.test = new PIXI.Sprite(P3.assets.texture('ui_function_char_ben'));
		}
	}

	animateIn() {
		super.animateIn();

		new P3.utils.TWEEN.Tween(this._fullTint, this.tweens)
			.to({
				alpha: 0
			}, 500)
			.easing(P3.utils.TWEEN.Easing.Linear.None)
			.delay(1750)
			.onComplete(() => {
				this.playIntroCamera();
			})
			.start();

		this.commandinterface.resize();	// quick hack to update the block display count for a tutorial.
	}

	animateOut() {
		super.animateOut();
		this.backgroundMusic.stop();
		P3.audio.stopMusic();
	}

	playIntroCamera() {
		if (!this.world.cameraPath) {
			this.cameraButton.visible = true;
			this.commandinterface.ui.visible = true;

			if (this.tutorialMode) {
				this.overlay.start();
			}

			return;
		}

		const firstNode = this.world.cameraPath[0];
		const totalTime = 3000;

		if (!firstNode) {		// If there's no camera paths available, we just enable the level as normal and continue.
			this.cameraButton.visible = true;
			this.commandinterface.ui.visible = true;
			this.cameraController.sideControls.enabled = true;
			this.stage.add(this.camera);

			const pos = this.player.mesh.position;

			this.camera.position.copy(pos);
			this.camera.position.add(new THREE.Vector3(1.8, 2.6, 6));
			this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));

			return;
		}

		this.stage.add(this.camera);

		this.player.mesh.quaternion.copy(this.camera.quaternion);

		const bez = [];

		for (let i = 1; i < this.world.cameraPath.length; i++) {
			bez.push({
				x : this.world.cameraPath[i].x,
				y : this.world.cameraPath[i].y,
				z : this.world.cameraPath[i].z
			});
		}

		const ppos = this.player.mesh.position.clone().add(new THREE.Vector3(1.8, 2.6, 6));

		bez.push({x: ppos.x, y: ppos.y, z: ppos.z});

		TweenMax.to(this.camera.position, totalTime / 2000,
			{
				delay         : 0,
				bezier        : bez,
				ease          : Sine.easeInOut,
				onUpdateScope : this,
				onStart       : () => {
					this.cameraTweening = true;
					this.cameraController.sideControls.enabled = false;
				},
				onUpdate: () => {
					this.camera.lookAt(this.player.mesh.position.x + 1.8, this.player.mesh.position.y + 2.6, this.player.mesh.position.z);
					this.player.mesh.lookAt(this.camera.position.x - 1.8, this.camera.position.y - 2.6, this.camera.position.z);
				},
				onComplete: () => {
					this.cameraTweening = false;
					this.cameraButton.visible = true;
					this.commandinterface.ui.visible = true;
					this.cameraController.sideControls.enabled = true;
					this.cameraController.ready = true;
					const pos = this.player.mesh.position;
					this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));

					if (this.tutorialMode) {
						this.overlay.start();
					}
				}
			});
	}

	tweenToStart(cb) {
		if (this.cameraTweening) {
			return;
		}

		if (this.camera.position.x === this.player.mesh.position.x + 1.8) {
			cb && cb();

			return;
		}
		this.cameraTweening = true;

		new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
			.to({
				x: this.player.mesh.position.x + 1.8
			}, 500)
			.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			.onUpdate(() => {
				this.cameraController.sideControls.target.x = this.camera.position.x;
			})
			.onComplete(() => {
				this.cameraTweening = false;
				this.cameraController.overheadControls.enabled = false;
				this.cameraController.sideControls.enabled = true;
				this.cameraController.sideControls.target.copy(this.player.mesh.position);
				this.cameraController.sideControls.target.y += 2.6;
				this.cameraController.sideControls.target.x += 1.8;
				const pos = this.player.mesh.position;

				this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));
				cb && cb();
			})
			.start();
	}

	toggleOverheadCamera(cb = null) {
		if (this.cameraTweening) {
			return;
		}

		this.overheadActive = !this.overheadActive;

		if (common.currentLevel === 2 && this.tutorialMode && this.overlay.stage < 3) {
			this.overlay.stage++;

			if (this.overlay.stage === 1) {
				this.overlay.updateTutorial(this.overlay.stage);
			} else {
				this.tutorialMode = false;
				this.overlay.stop();
				this.runButton.position.y = common.STAGE_HEIGHT - 100;
				this.commandinterface.ui.interactive = true;
				this.commandinterface.ui.interactiveChildren = true;
				common.focusOverlay.visible = false;
			}
		}

		if (this.overheadActive) {
			this.tweens && this.tweens.removeAll();
			this.stage.add(this.camera);

			const tile = this.player.tile;
			const ppos = this.player.mesh.position;

			this.camera.position.copy(this.player.mesh.position);
			this.camera.position.add(new THREE.Vector3(1.8, 2.6, 6));
			this.camera.lookAt(new THREE.Vector3(ppos.x + 1.8, ppos.y + 2.6, ppos.z));

			new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
				.to({
					x : tile.position3D.x + 1.8,
					y : tile.position3D.y + 20,
					z : tile.position3D.z
				}, 1000)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.onStart(() => {
					this.cameraController.sideControls.enabled = false;
					this.cameraController.overheadControls.enabled = false;
					this.cameraController.overheadControls.resetSoft();
					this.cameraTweening = true;
					this.player.mesh.icon.visible = true;
					this.player.mesh.spine.visible = false;
				})
				.onUpdate(() => {
					this.player.mesh.spine.quaternion.copy(this.camera.quaternion);
					this.camera.lookAt(tile.position3D.x + 1.8, tile.position3D.y + 2.6, tile.position3D.z);
				})
				.onComplete(() => {
					this.cameraTweening = false;
					this.cameraController.overheadControls.enabled = true;
					this.cameraController.overheadControls.target.copy(this.player.mesh.position);
					this.cameraController.overheadControls.target.x += 1.8;
					cb && cb();
				})
				.start();
		} else {
			this.tweens && this.tweens.removeAll();
			this.runButton.visible = false;
			this.cameraController.overheadControls.enabled = false;
			this.cameraController.sideControls.enabled = false;

			new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
				.to({
					x : this.player.mesh.position.x + 1.8,
					z : this.player.mesh.position.z
				}, 500)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.onStart(() => {
					this.cameraTweening = true;
				})
				.onComplete(() => {
					const tile = this.player.tile;

					new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
						.to({
							x : tile.position3D.x + 1.8,
							y : tile.position3D.y + 2.6,
							z : tile.position3D.z + 6
						}, 1000)
						.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
						.onUpdate(() => {
							this.player.mesh.spine.quaternion.copy(this.camera.quaternion);
							this.camera.lookAt(tile.position3D.x + 1.8, tile.position3D.y + 2.6, tile.position3D.z);
						})
						.onComplete(() => {
							this.runButton.visible = this.commandinterface.commands.length > 2;
							this.cameraTweening = false;
							this.player.mesh.icon.visible = false;
							this.player.mesh.spine.visible = true;
							this.cameraController.overheadControls.enabled = false;
							this.cameraController.sideControls.enabled = true;
							this.cameraController.sideControls.target.copy(this.player.mesh.position);
							this.cameraController.sideControls.target.add(new THREE.Vector3(1.8, 2.6, 0));
							const pos = this.player.mesh.position;

							this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));
							cb && cb();
						})
						.start();
				})
				.start();
		}
	}

	resize() {
		super.resize();
		this.commandinterface.windowResize();
		this.pauseButton.position.set(((common.STAGE_WIDTH + P3.View.width) * 0.5) - 75, 75);
		this.cameraButton.position.set(((common.STAGE_WIDTH + P3.View.width) * 0.5) - 100, common.STAGE_HEIGHT - 100);

		if (this.focusOverlay) {
			this.focusOverlay.resize();
		}
	}

	toggleCamera() {
		this.cameraController.sideControls.resetSoft();
		this.cameraController.sideControls.enabled = true;
		this.cameraController.sideControls.target.copy(this.player.mesh.position);
		this.cameraController.sideControls.target.add(new THREE.Vector3(1.8, 2.6, 0));
		this.stage.add(this.camera);

		const pos = this.player.mesh.position;
		this.camera.position.copy(pos).add(new THREE.Vector3(1.8, 2.6, 6));
		this.camera.lookAt(new THREE.Vector3(pos.x + 1.8, pos.y + 2.6, pos.z));
	}

	displayUnusedCommands(commands) {
		const completedLevels = JSON.parse(localStorage.getItem('completed_levels'));

		if (!commands || commands.length <= 0) {
			if (!this.playButton) {
				P3.utils.delay(1, () => {
					return this.signals.next.dispatch(completedLevels[common.currentLevel]);
				});
			} else {
				this.playButton.visible = true;
			}

			return;
		}

		for (let i = 0; i < commands.length; i++) {
			const crossout = new PIXI.Sprite(P3.assets.texture('ui_function_cross'));

			crossout.anchor.set(0, 0.5);
			crossout.scale.set(common.commandScale);
			crossout.visible = false;
			crossout.position.set(commands[i].x - (commands[i].width * 0.5), commands[i].y);
			commands[i].parent.addChild(crossout);

			P3.utils.delay(i * 0.3, () => {
				crossout.visible = true;
				commands[i].bg.texture = P3.assets.texture('ui_function_turn_bg_9slice'); // this is the most white texture we have so tints look right
				commands[i].bg.tint = 0xFF0000;

				if (i === commands.length - 1) {
					if (!this.playButton) {
						P3.utils.delay(1, () => {
							return this.signals.next.dispatch(completedLevels[common.currentLevel]);
						});
					} else {
						this.playButton.visible = true;
					}
				}
			});
		}
	}

	animateCoin(values, unusedCommands) {
		this.saveData(values);

		P3.audio.playSound('music_level_complete_sting_00');

		const goaltile = this.world.goal.objects.find((element) => {
			return element instanceof GoalTile;
		});

		goaltile.stopAnim();

		let startupSpin = null;

		const xpad = -1.8; const zpad = 0;

		const coin = goaltile.coin;

		new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
			.to({
				x : this.camera.position.x + xpad,
				z : this.camera.position.z + zpad
			}, 500)
			.easing(P3.utils.TWEEN.Easing.Linear.None)
			.onComplete(() => {
				if (!coin) {
					P3.utils.delay(1, () => {
						this.displayUnusedCommands(unusedCommands);
					});
				}
			})
			.start();

		const delay = coin ? 3000 : 0;

		new P3.utils.TWEEN.Tween(this.backgroundMusic.howl, this.tweens)
			.to({
				_volume: 0
			}, 3000)
			.easing(P3.utils.TWEEN.Easing.Linear.None)
			.delay(delay)
			.onUpdate(() => {
				this.backgroundMusic.howl.volume(this.backgroundMusic.howl._volume, this.backgroundMusic.id);
			})
			.start();

		if (!coin) {
			return;
		}

		this.collectablesPanel.visible = false;

		new P3.utils.TWEEN.Tween(coin.position, this.tweens)
			.to({
				y: coin.position.y + 3.75
			}, 1000)
			.easing(P3.utils.Easing.Back2.Out)
			.onStart(() => {
				coin.renderOrder = 1;
			})
			.onComplete(() => {
				return startupSpin.start();
			})
			.start();

		new P3.utils.TWEEN.Tween(this.camera.position, this.tweens)
			.to({
				y: this.camera.position.y + 1
			}, 1000)
			.easing(P3.utils.Easing.Back2.Out)
			.start();

		// eslint-disable-next-line no-nested-ternary
		const spinPad = this.player.direction === Player.DIRECTION.UP ? 90 : this.player.direction === Player.DIRECTION.DOWN ? -90 : 0;

		startupSpin = new P3.utils.TWEEN.Tween(coin.rotation, this.tweens)
			.to({
				y: coin.rotation.y + ((2520 + spinPad) * PIXI.DEG_TO_RAD)
			}, 1750)
			.easing(P3.utils.TWEEN.Easing.Quadratic.In)
			.onStart(() => {
				const emitter = this.world.dataParticles;
				coin.getWorldPosition(emitter.position);
				emitter.play();
			})
			.onComplete(() => {
				P3.audio.playSound('sfx_achievement_unlocked_00');

				P3.utils.delay(0.5, () => {
					this.showUnlockUI(unusedCommands);
					coin.renderOrder = 0;
				});
			});
	}

	showUnlockUI(unusedCommands) {
		let unlockText = null;

		const bg = new PIXI.Graphics();

		bg.beginFill(0x0, 0);
		bg.drawRect(0, 0, common.STAGE_WIDTH, common.STAGE_HEIGHT);
		bg.endFill();
		bg.interactive = true;
		this.addChild(bg);
		this.addChild(this.pauseButton);// reset to top - user should always be able to pause.

		this.greenParticles = new ParticleEmitter('particle_endlvl_green', [
			P3.assets.texture('particle_endlvl_green')
		], true);
		this.greenParticles.enableLoop();
		this.greenParticles.play();
		this.greenParticles.position.set((common.STAGE_WIDTH * 0.5) - 100, common.STAGE_HEIGHT - 150);
		this.addChild(this.greenParticles);

		const panelBG = new PIXI.Sprite(P3.assets.texture('endlvl_panel'));

		panelBG.anchor.set(0.5, 1);
		panelBG.position.set((common.STAGE_WIDTH * 0.5), common.STAGE_HEIGHT - 50);
		this.addChild(panelBG);

		let text = '';
		if (common.currentLevel === 1 || common.currentLevel === 5) {
			text = `${translate(common.levelSettings.unlock.text)}\n${translate('GAME.UNLOCKS.UNLOCK')}`;
		} else {
			text = `${translate('GAME.UNLOCKS.CODE_UNLOCK')}: '${translate(common.levelSettings.unlock.text)}'\n${translate('GAME.UNLOCKS.UNLOCK')}`;
		}

		unlockText = Text(text, {font: '46px Ahkio Regular', align: 'center'});
		unlockText.tint = 0xFFFFFF;
		unlockText.anchor.set(0.5, 1);
		unlockText.position.set(0, -10);
		panelBG.addChild(unlockText);
		const states = new P3.ButtonStates();

		states.normal 	= P3.assets.texture('ui_btn_large_out');
		states.over 	= P3.assets.texture('ui_btn_large_over');
		states.down 	= P3.assets.texture('ui_btn_large_down');
		states.icon 	= P3.assets.texture('ui_icon_play');

		this.playButton = new P3.Button(states);
		this.playButton.signals.click.add(() => {
			const completedLevels = JSON.parse(localStorage.getItem('completed_levels'));

			this.signals.next.dispatch(completedLevels[common.currentLevel]);
		}, this);
		this.playButton.clickSoundName = 'sfx_btn_press_00';
		this.playButton.overSoundName = 'sfx_btn_rollover_00';
		this.playButton.visible = false;
		this.playButton.position.set((common.STAGE_WIDTH + P3.View.width) * 0.5 - 110, common.STAGE_HEIGHT - 110);
		this.addChild(this.playButton);

		P3.utils.delay(1, () => {
			this.displayUnusedCommands(unusedCommands);
		});
	}

	saveData(values = {commands: 0, steps: 0, coins: 0}) {
		let completedLevels = JSON.parse(localStorage.getItem('completed_levels'));

		if (!completedLevels) {
			completedLevels = {};
		}

		const stars = this.determineStars(values.commands);
		const score = this.calcScore(common.levelSettings.minimumBlocks - values.commands);

		let highscore = score;

		if (completedLevels[common.currentLevel] && completedLevels[common.currentLevel].highscore) {
			highscore = score > completedLevels[common.currentLevel].highscore ? score : completedLevels[common.currentLevel].highscore;
		}

		completedLevels[common.currentLevel] = {
			score     : stars,						// stars
			points    : score,						// actual score
			coins     : values.coins,				// number of coins
			steps     : values.steps,				// number of steps
			blocks    : values.commands,			// number of commands
			highscore : highscore // new HS
		};

		localStorage.setItem('completed_levels', JSON.stringify(completedLevels));
	}

	determineStars(blocks) {
		const min = common.levelSettings.minimumBlocks;

		if (blocks <= min) { // If the user is better or the same as the minimum steps
			return 3;
		} else if (blocks <= min + 1) { // If the user got one more than the minimum
			return 2;
		} else if (blocks <= min + 2) { // If the user got two more than the minimum
			return 1;
		}

		return 0; 							// If the user got more than three than the minimum
	}

	calcScore(blockdifference) {
		blockdifference = Math.max(0, blockdifference);
		return common.scoring.points + (blockdifference * common.scoring.command);
	}
}
